import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Loader2, Plus, ArrowRight } from "lucide-react";
import { listLauraAudits } from "@/app/lib/auditflowApi";
import type { LauraAudit, LauraStageId } from "@/app/components/shared/types";
import { LauraNewAuditModal } from "@/app/components/laura/LauraNewAuditModal";
import { LAURA_STAGES, stageLabel } from "@/app/components/laura/stages";
import { useTranslation } from "@/app/i18n/LocaleContext";

const PLANNING_STAGES: LauraStageId[] = [
    "p0", "p1", "p2", "p3", "p4", "p5", "p6", "p7",
];

function planningProgress(audit: LauraAudit): number {
    const completed = (audit.stage_completions ?? []).filter((c) =>
        PLANNING_STAGES.includes(c.stage),
    ).length;
    return Math.round((completed / PLANNING_STAGES.length) * 100);
}

export function LauraDashboard() {
    const { t, locale } = useTranslation();
    const navigate = useNavigate();
    const [audits, setAudits] = useState<LauraAudit[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    function load() {
        setLoading(true);
        listLauraAudits()
            .then(setAudits)
            .catch(() => setAudits([]))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        load();
    }, []);

    const stats = useMemo(() => {
        const active = audits.filter((a) => a.phase === "planning").length;
        const completedPlanning = audits.filter((a) => {
            const done = (a.stage_completions ?? []).filter((c) =>
                PLANNING_STAGES.includes(c.stage),
            ).length;
            return done === PLANNING_STAGES.length;
        }).length;
        const inProgress = audits.filter((a) => {
            const done = (a.stage_completions ?? []).filter((c) =>
                PLANNING_STAGES.includes(c.stage),
            ).length;
            return done > 0 && done < PLANNING_STAGES.length;
        }).length;
        const pendingApproval = audits.filter(
            (a) => a.current_stage && PLANNING_STAGES.includes(a.current_stage),
        ).length;
        return { active, completedPlanning, inProgress, pendingApproval };
    }, [audits]);

    function openAudit(id: string) {
        navigate(`/laura/${id}`);
    }

    return (
        <div className="flex h-full flex-col bg-slate-50">
            <header className="flex items-center justify-between border-b bg-white px-6 py-4">
                <div className="flex items-center gap-2">
                    <Landmark className="h-5 w-5 text-slate-700" />
                    <h1 className="text-xl font-medium">{t("laura", "dashboardTitle")}</h1>
                </div>
                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white"
                >
                    <Plus className="h-4 w-4" />
                    {t("laura", "newAudit")}
                </button>
            </header>

            <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        { label: t("laura", "statActive"), value: stats.active },
                        { label: t("laura", "statInProgress"), value: stats.inProgress },
                        { label: t("laura", "statPlanningDone"), value: stats.completedPlanning },
                        { label: t("laura", "statPending"), value: stats.pendingApproval },
                    ].map((s) => (
                        <div
                            key={s.label}
                            className="rounded-xl border bg-white p-4 shadow-sm"
                        >
                            <p className="text-sm text-gray-500">{s.label}</p>
                            <p className="mt-1 text-2xl font-semibold text-slate-900">
                                {s.value}
                            </p>
                        </div>
                    ))}
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                ) : audits.length === 0 ? (
                    <p className="text-center text-sm text-gray-500 py-12">
                        {t("laura", "empty")}
                    </p>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {audits.map((a) => {
                            const progress = planningProgress(a);
                            const stageMeta = LAURA_STAGES.find(
                                (s) => s.id === a.current_stage,
                            );
                            return (
                                <button
                                    key={a.id}
                                    type="button"
                                    onClick={() => openAudit(a.id)}
                                    className="group rounded-xl border bg-white p-5 text-left shadow-sm transition hover:border-slate-300 hover:shadow-md"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <h2 className="font-medium text-slate-900 line-clamp-2">
                                            {a.title}
                                        </h2>
                                        <ArrowRight className="h-4 w-4 shrink-0 text-gray-400 group-hover:text-slate-700" />
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                                            {a.laura_sai_profiles?.name ?? "SAI"}
                                        </span>
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-xs ${
                                                stageMeta?.phase === "planning"
                                                    ? "bg-blue-100 text-blue-800"
                                                    : "bg-emerald-100 text-emerald-800"
                                            }`}
                                        >
                                            {stageLabel(a.current_stage, locale)}
                                        </span>
                                    </div>
                                    <div className="mt-4">
                                        <div className="mb-1 flex justify-between text-xs text-gray-500">
                                            <span>{t("laura", "planningProgress")}</span>
                                            <span>{progress}%</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                                            <div
                                                className="h-full rounded-full bg-emerald-600 transition-all"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </div>
                                    <p className="mt-3 text-xs text-gray-400">
                                        {t("laura", "lastUpdated")}:{" "}
                                        {new Date(a.updated_at).toLocaleDateString()}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <LauraNewAuditModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreated={(id) => {
                    setModalOpen(false);
                    openAudit(id);
                }}
            />
        </div>
    );
}
